// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductImagesBadges from "@eisinc/App/Components/CustomBadges/ProductImagesBadges";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { HasIsLoading } from "@eisinc/XDL/types";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    // XNG-Changes
    extendedStyles?: ProductListProductInformationStyles;
    showCustomBadges?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    // XNG-Changes
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext & HasIsLoading;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    // XNG-Changes
    badgesContainerStyle?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
};

// const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    // XNG-Changes
    isLoading,
    extendedStyles,
    showCustomBadges,
    isTabletScreen,
}: Props) => {
    const { product } = productContext;
    // <XNG-Changes>
    const [styles] = useState(() => mergeToNew(productInformationStyles, extendedStyles));

    if (isLoading) {
        return (
            <GridContainer {...styles.container}>
                {showTitle && (
                    <GridItem {...styles.descriptionGridItem}>
                        <Skeleton height={isTabletScreen ? "51px" : "34px"} width="100%" />
                    </GridItem>
                )}
                {showPartNumbers && (
                    <GridItem {...styles.partNumbersGridItem}>
                        <Skeleton height="30px" width="100%" />
                        <Skeleton height="14px" width="100%" />
                    </GridItem>
                )}
                {showAvailability && (
                    <GridItem {...styles.availabilityGridItem}>
                        <Skeleton height="14px" width="100%" />
                    </GridItem>
                )}
                {showCustomBadges && (
                    <GridItem width={12}>
                        <Skeleton height="20px" width="100%" />
                    </GridItem>
                )}
            </GridContainer>
        );
    }
    // </XNG-Changes>

    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // <XNG-Changes>

    const badges = (product.properties?.["badges"] && JSON.parse(product.properties["badges"])) || [];

    const customBadges = badges.length > 0 && <ProductImagesBadges product={product} badges={badges} />;
    // </XNG-Changes>

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {/* {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )} */}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription
                        {...{ widgetId: "PLP" }}
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                    />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                        // XNG-Changes
                        showLabel={true}
                        {...({
                            modelNumber: product.detail?.modelNumber || "",
                            specNumber: product.properties.specNumber || "",
                        } as any)}
                    />
                    {/* <XNG-Changes> */}
                    {product.brand && showBrand && (
                        <ProductBrand
                            brand={product.brand}
                            extendedStyles={styles.brandStyles}
                            {...{ showLabel: true }}
                        />
                    )}
                    {/* </XNG-Changes> */}
                </GridItem>
            )}
            {/* <XNG-Changes> */}
            {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {showCustomBadges && customBadges}
            {/* </XNG-Changes> */}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {/* {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )} */}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
